import { useState, useEffect } from "react";
import axios from "axios";
import { LogOut, Plus, Pencil, Trash2, X, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_SITE = { name: "", address: "", lat: "", lng: "", status: "available", zone: "", notes: "" };

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [sites, setSites] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_SITE);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [tab, setTab] = useState("sites");
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsError, setAnalyticsError] = useState("");

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchSites = async () => {
    try {
      const res = await axios.get(`${API}/sites`);
      setSites(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (token) fetchSites();
  }, [token]);

  useEffect(() => {
    if (token && tab === "analytics") {
      setAnalytics(null);
      setAnalyticsError("");
      axios.get(`${API}/admin/analytics/dashboard`, { params: { days: analyticsDays }, headers: authHeaders })
        .then((res) => setAnalytics(res.data))
        .catch(() => setAnalyticsError("Analytics temporarily unavailable. Please try again."));
    }
  }, [token, tab, analyticsDays]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      const t = res.data.token;
      setToken(t);
      localStorage.setItem("admin_token", t);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoginError(typeof detail === "string" ? detail : "Login failed");
    }
  };

  const handleLogout = () => {
    setToken("");
    localStorage.removeItem("admin_token");
    axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
  };

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_SITE);
    setShowForm(true);
  };

  const openEdit = (site) => {
    setEditing(site.id);
    setForm({ name: site.name, address: site.address, lat: site.lat, lng: site.lng, status: site.status, zone: site.zone, notes: site.notes });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, lat: parseFloat(form.lat), lng: parseFloat(form.lng) };
      if (editing) {
        await axios.put(`${API}/sites/${editing}`, payload, { headers: authHeaders });
      } else {
        await axios.post(`${API}/sites`, payload, { headers: authHeaders });
      }
      setShowForm(false);
      setEditing(null);
      await fetchSites();
    } catch (err) {
      alert(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/sites/${id}`, { headers: authHeaders });
      await fetchSites();
    } catch (err) {
      alert(err.response?.data?.detail || "Delete failed");
    }
  };

  const handleGeocode = async () => {
    if (!form.address.trim()) { alert("Enter an address first"); return; }
    setGeocoding(true);
    try {
      const res = await axios.get(`${API}/geocode`, { params: { address: form.address } });
      setForm({ ...form, lat: res.data.lat, lng: res.data.lng });
    } catch (err) {
      alert(err.response?.data?.detail || "Could not find coordinates for that address. Try a more specific address.");
    } finally {
      setGeocoding(false);
    }
  };

  // Login screen
  if (!token) {
    return (
      <div style={{ backgroundColor: "#FFFFFF", minHeight: "calc(100vh - 80px)" }}>
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-32 md:py-44">
          <div className="max-w-md">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold mb-6" style={{ color: "#d4782f" }}>Admin Access</p>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-10" style={{ color: "#1A1D23" }}>Sign In</h1>
            <form onSubmit={handleLogin}>
              <div className="mb-5">
                <label className="text-xs uppercase tracking-[0.15em] font-semibold block mb-3" style={{ color: "rgba(255,255,255,0.5)" }}>Email</label>
                <input type="email" data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="w-full px-5 py-4 text-sm rounded-none border-none focus:outline-none"
                  style={{ backgroundColor: "#FFFFFF", color: "#1A1D23" }}
                  onFocus={(e) => (e.target.style.boxShadow = "0 0 0 2px #d4782f")}
                  onBlur={(e) => (e.target.style.boxShadow = "none")} />
              </div>
              <div className="mb-6">
                <label className="text-xs uppercase tracking-[0.15em] font-semibold block mb-3" style={{ color: "rgba(255,255,255,0.5)" }}>Password</label>
                <input type="password" data-testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="w-full px-5 py-4 text-sm rounded-none border-none focus:outline-none"
                  style={{ backgroundColor: "#FFFFFF", color: "#1A1D23" }}
                  onFocus={(e) => (e.target.style.boxShadow = "0 0 0 2px #d4782f")}
                  onBlur={(e) => (e.target.style.boxShadow = "none")} />
              </div>
              {loginError && <p data-testid="admin-login-error" className="text-sm mb-4" style={{ color: "#e74c3c" }}>{loginError}</p>}
              <button type="submit" data-testid="admin-login-submit"
                className="px-8 py-4 font-bold tracking-wide text-sm rounded-none transition-colors"
                style={{ backgroundColor: "#d4782f", color: "#FFFFFF" }}>
                Sign In
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Admin dashboard
  return (
    <div style={{ backgroundColor: "#F4F5F7", minHeight: "calc(100vh - 80px)" }}>
      <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-10 md:py-16">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold mb-2" style={{ color: "#d4782f" }}>Admin Panel</p>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ color: "#1A1D23" }}>
              {tab === "sites" ? `Manage PadSite Locations (${sites.length})` : "Website Analytics"}
            </h1>
          </div>
          <div className="flex gap-3">
            {tab === "sites" && (
            <button data-testid="admin-add-site" onClick={openAdd}
              className="inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.1em] font-bold rounded-none"
              style={{ backgroundColor: "#d4782f", color: "#FFFFFF" }}>
              <Plus size={16} /> Add Site
            </button>
            )}
            <button data-testid="admin-logout" onClick={handleLogout}
              className="inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.1em] font-bold rounded-none border"
              style={{ borderColor: "#3A3A3A", color: "#4A4F5C" }}>
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-3 mb-8">
          {[{ key: "sites", label: "Sites" }, { key: "analytics", label: "Analytics" }].map((t) => (
            <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)}
              className="px-5 py-2 text-xs uppercase tracking-[0.15em] font-semibold transition-colors rounded-none"
              style={{
                backgroundColor: tab === t.key ? "#d4782f" : "transparent",
                color: tab === t.key ? "#FFFFFF" : "#3A3A3A",
                border: `1px solid ${tab === t.key ? "#d4782f" : "rgba(58,58,58,0.3)"}`,
              }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Add/Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: "rgba(0,0,0,0.6)" }}>
            <div className="w-full max-w-lg mx-4 p-8 max-h-[90vh] overflow-y-auto" style={{ backgroundColor: "#FFFFFF" }}>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold" style={{ color: "#1A1D23" }}>
                  {editing ? "Edit Site" : "Add New Site"}
                </h3>
                <button onClick={() => setShowForm(false)} data-testid="admin-form-close"><X size={20} style={{ color: "#4A4F5C" }} /></button>
              </div>
              <form onSubmit={handleSave}>
                <div className="grid grid-cols-1 gap-4 mb-4">
                  <input data-testid="admin-site-name" placeholder="Site Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-3 text-sm border rounded-none focus:outline-none" style={{ borderColor: "#16191E", color: "#1A1D23" }}
                    onFocus={(e) => (e.target.style.borderColor = "#d4782f")} onBlur={(e) => (e.target.style.borderColor = "#16191E")} />
                  <input data-testid="admin-site-address" placeholder="Address (e.g. 123 Main St, Atlanta, GA)" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                    className="w-full px-4 py-3 text-sm border rounded-none focus:outline-none" style={{ borderColor: "#16191E", color: "#1A1D23" }}
                    onFocus={(e) => (e.target.style.borderColor = "#d4782f")} onBlur={(e) => (e.target.style.borderColor = "#16191E")} />
                  <button type="button" data-testid="admin-geocode-btn" onClick={handleGeocode} disabled={geocoding}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.1em] font-bold rounded-none transition-colors disabled:opacity-50 border-2"
                    style={{ backgroundColor: "#d4782f", color: "#1A1D23", borderColor: "#d4782f" }}>
                    <MapPin size={16} /> {geocoding ? "Looking up..." : "Lookup Coordinates from Address"}
                  </button>
                  <div className="grid grid-cols-2 gap-4">
                    <input data-testid="admin-site-lat" placeholder="Latitude" required type="number" step="any" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })}
                      className="w-full px-4 py-3 text-sm border rounded-none focus:outline-none" style={{ borderColor: "#16191E", color: "#1A1D23" }}
                      onFocus={(e) => (e.target.style.borderColor = "#d4782f")} onBlur={(e) => (e.target.style.borderColor = "#16191E")} />
                    <input data-testid="admin-site-lng" placeholder="Longitude" required type="number" step="any" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })}
                      className="w-full px-4 py-3 text-sm border rounded-none focus:outline-none" style={{ borderColor: "#16191E", color: "#1A1D23" }}
                      onFocus={(e) => (e.target.style.borderColor = "#d4782f")} onBlur={(e) => (e.target.style.borderColor = "#16191E")} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <select data-testid="admin-site-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                      className="w-full px-4 py-3 text-sm border rounded-none focus:outline-none appearance-none" style={{ borderColor: "#16191E", color: "#1A1D23" }}>
                      <option value="available">Available</option>
                      <option value="leased">Leased</option>
                      <option value="pending">Pending</option>
                    </select>
                    <input data-testid="admin-site-zone" placeholder="Zone" value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })}
                      className="w-full px-4 py-3 text-sm border rounded-none focus:outline-none" style={{ borderColor: "#16191E", color: "#1A1D23" }}
                      onFocus={(e) => (e.target.style.borderColor = "#d4782f")} onBlur={(e) => (e.target.style.borderColor = "#16191E")} />
                  </div>
                  <textarea data-testid="admin-site-notes" placeholder="Notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full px-4 py-3 text-sm border rounded-none focus:outline-none resize-none" style={{ borderColor: "#16191E", color: "#1A1D23" }}
                    onFocus={(e) => (e.target.style.borderColor = "#d4782f")} onBlur={(e) => (e.target.style.borderColor = "#16191E")} />
                </div>
                <button type="submit" data-testid="admin-site-save" disabled={saving}
                  className="px-6 py-3 font-bold tracking-wide text-sm rounded-none transition-colors disabled:opacity-50"
                  style={{ backgroundColor: "#d4782f", color: "#FFFFFF" }}>
                  {saving ? "Saving..." : editing ? "Update Site" : "Add Site"}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Sites Table */}
        {tab === "sites" && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ backgroundColor: "#FFFFFF" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #d4782f" }}>
                {["Name", "Zone", "Status", "Lat / Lng", "Actions"].map((h) => (
                  <th key={h} className="text-left px-5 py-4 text-xs uppercase tracking-[0.1em] font-semibold" style={{ color: "#1A1D23" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sites.map((site) => (
                <tr key={site.id} className="border-b" style={{ borderColor: "#16191E" }}>
                  <td className="px-5 py-4">
                    <span className="font-bold" style={{ color: "#1A1D23" }}>{site.name}</span>
                    <br />
                    <span className="text-xs" style={{ color: "#4A4F5C" }}>{site.address}</span>
                  </td>
                  <td className="px-5 py-4" style={{ color: "#4A4F5C" }}>{site.zone}</td>
                  <td className="px-5 py-4">
                    <span className="text-xs uppercase tracking-[0.1em] font-semibold px-2 py-1"
                      style={{
                        color: site.status === "available" ? "#4EA72E" : site.status === "leased" ? "#d4782f" : "#0F9ED5",
                        backgroundColor: (site.status === "available" ? "#4EA72E" : site.status === "leased" ? "#d4782f" : "#0F9ED5") + "15",
                      }}>
                      {site.status}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-xs" style={{ color: "#4A4F5C" }}>{site.lat.toFixed(4)}, {site.lng.toFixed(4)}</td>
                  <td className="px-5 py-4">
                    <div className="flex gap-2">
                      <button data-testid={`admin-edit-${site.id}`} onClick={() => openEdit(site)}
                        className="p-2 transition-colors" style={{ color: "#d4782f" }}>
                        <Pencil size={16} />
                      </button>
                      <button data-testid={`admin-delete-${site.id}`} onClick={() => handleDelete(site.id, site.name)}
                        className="p-2 transition-colors" style={{ color: "#e74c3c" }}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

        {/* Analytics View */}
        {tab === "analytics" && (
          <div data-testid="admin-analytics">
            <div className="flex flex-wrap gap-3 mb-6">
              {[7, 30, 90].map((d) => (
                <button key={d} data-testid={`analytics-days-${d}`} onClick={() => setAnalyticsDays(d)}
                  className="px-4 py-2 text-xs uppercase tracking-[0.1em] font-semibold rounded-none transition-colors"
                  style={{
                    backgroundColor: analyticsDays === d ? "#1A1D23" : "transparent",
                    color: analyticsDays === d ? "#FFFFFF" : "#3A3A3A",
                    border: `1px solid ${analyticsDays === d ? "#1A1D23" : "rgba(58,58,58,0.3)"}`,
                  }}>
                  {d} Days
                </button>
              ))}
            </div>

            {analyticsError && <p data-testid="analytics-error" className="text-sm mb-4" style={{ color: "#e74c3c" }}>{analyticsError}</p>}
            {!analytics && !analyticsError && <p data-testid="analytics-loading" className="text-sm" style={{ color: "#4A4F5C" }}>Loading analytics...</p>}

            {analytics && !analytics.configured && (
              <div className="p-8" style={{ backgroundColor: "#FFFFFF", border: "1px solid rgba(26,29,35,0.08)" }} data-testid="analytics-not-configured">
                <h3 className="text-lg font-bold mb-3" style={{ color: "#1A1D23" }}>Almost there — one step left</h3>
                <p className="text-sm leading-relaxed" style={{ color: "#4A4F5C" }}>
                  Your Google Analytics connection (Property 553950049) is ready. To finish setup, share the contents of your
                  service account key file (padsite-analytics-5e2295effe8f.json) — open it with any text editor and copy everything inside.
                  Stats will appear here automatically once it's added.
                </p>
              </div>
            )}

            {analytics && analytics.configured && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {[
                    { label: "Active Visitors", value: analytics.active_users?.[0]?.metrics?.[0] ?? "0", testid: "analytics-active-users" },
                    { label: "Page Views", value: analytics.page_views?.[0]?.metrics?.[0] ?? "0", testid: "analytics-page-views" },
                  ].map((s) => (
                    <div key={s.label} className="p-8" style={{ backgroundColor: "#FFFFFF", border: "1px solid rgba(26,29,35,0.08)" }} data-testid={s.testid}>
                      <p className="text-xs uppercase tracking-[0.15em] font-semibold mb-2" style={{ color: "#d4782f" }}>{s.label}</p>
                      <p className="text-4xl font-bold tracking-tight" style={{ color: "#1A1D23", fontFamily: "'Space Grotesk', sans-serif" }}>{Number(s.value).toLocaleString()}</p>
                      <p className="text-xs mt-1" style={{ color: "#4A4F5C" }}>Last {analytics.period_days} days</p>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="p-8" style={{ backgroundColor: "#FFFFFF", border: "1px solid rgba(26,29,35,0.08)" }} data-testid="analytics-top-pages">
                    <h3 className="text-base font-bold mb-5" style={{ color: "#1A1D23" }}>Top Pages</h3>
                    {analytics.top_pages?.length ? analytics.top_pages.map((r, i) => (
                      <div key={i} className="flex justify-between items-center py-2.5 border-b" style={{ borderColor: "rgba(26,29,35,0.06)" }}>
                        <span className="text-sm truncate pr-4" style={{ color: "#4A4F5C" }}>{r.dimensions[1] || r.dimensions[0]}</span>
                        <span className="text-sm font-bold flex-shrink-0" style={{ color: "#d4782f" }}>{Number(r.metrics[0]).toLocaleString()}</span>
                      </div>
                    )) : <p className="text-sm" style={{ color: "#4A4F5C" }}>No page data yet — check back after your first visitors.</p>}
                  </div>
                  <div className="p-8" style={{ backgroundColor: "#FFFFFF", border: "1px solid rgba(26,29,35,0.08)" }} data-testid="analytics-traffic-sources">
                    <h3 className="text-base font-bold mb-5" style={{ color: "#1A1D23" }}>Traffic Sources</h3>
                    {analytics.traffic_sources?.length ? analytics.traffic_sources.map((r, i) => (
                      <div key={i} className="flex justify-between items-center py-2.5 border-b" style={{ borderColor: "rgba(26,29,35,0.06)" }}>
                        <span className="text-sm truncate pr-4" style={{ color: "#4A4F5C" }}>{r.dimensions[0]}</span>
                        <span className="text-sm font-bold flex-shrink-0" style={{ color: "#d4782f" }}>{Number(r.metrics[0]).toLocaleString()} sessions</span>
                      </div>
                    )) : <p className="text-sm" style={{ color: "#4A4F5C" }}>No traffic data yet.</p>}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
